/*
 * Copyright (c) Arc XP 2025.
 */

type ImaAdEventHandler = (event: google.ima.AdEvent | google.ima.AdErrorEvent) => void;

type AdsLoaderEventHandler = (
  event: google.ima.AdsManagerLoadedEvent | google.ima.AdErrorEvent
) => void;

// TODO: place the ad container in its own media-chrome controller

/** Manages video advertisements for a KoiPlayer instance by integrating with the Google IMA SDK. */
export class KoiAdsManager {
  /** The ad display container. */
  adDisplayContainer?: google.ima.AdDisplayContainer;

  /** The ads loader. */
  adsLoader?: google.ima.AdsLoader;

  /** The ads manager. */
  adsManager?: google.ima.AdsManager;

  /** The ad container element. */
  adContainer?: HTMLDivElement;

  /** The video element. */
  videoElement?: HTMLVideoElement;

  /** Whether an ad is currently playing. */
  isAdPlaying: boolean;

  /** Whether ads have been loaded. */
  adsLoaded: boolean;

  /** The ad tag URL. */
  adTagUrl: string;

  /** Initializes a new instance of the class with the given player instance. */
  constructor() {
    this.isAdPlaying = false;
    this.adsLoaded = false;
    this.adTagUrl = '';
  }

  /**
   * Initialize the IMA SDK
   *
   * This method will create an 'ad-container' element as a sibling element of the media element.
   * The ad-container element will overlay on top of koi-players normal video player.
   *
   * @param videoElement - The video element
   * @param adTagUrl - The ad tag URL
   */
  init(videoElement: HTMLVideoElement, adTagUrl: string): void {
    this.videoElement = videoElement;
    this.adTagUrl = adTagUrl;

    this.adContainer = document.createElement('div');
    this.adContainer.classList.add('ad-container');
    this.adContainer.style.position = 'absolute';
    this.adContainer.style.top = '0';
    this.adContainer.style.left = '0';
    this.adContainer.style.width = '100%';
    this.adContainer.style.height = '100%';
    this.adContainer.style.zIndex = '10';
    // Initially set pointer-events to none so clicks pass through to the play button
    this.disableAdContainerPointerEvents();

    if (videoElement.parentNode) {
      videoElement.parentNode.insertBefore(this.adContainer, videoElement.nextSibling);
    }
    this.initializeImaScript();
  }

  /**
   * Load the IMA SDK script
   *
   * @returns A promise that resolves when the script is loaded
   */
  loadImaScript(): Promise<void> {
    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = 'https://imasdk.googleapis.com/js/sdkloader/ima3.js';
      script.async = true;
      script.crossOrigin = 'anonymous';
      script.referrerPolicy = 'no-referrer-when-downgrade';
      script.onload = () => resolve();
      script.onerror = reject;
      document.head.appendChild(script);
    });
  }

  /** Set up the IMA SDK */
  setupIma(): void {
    if (!window.google || !window.google.ima || !this.adContainer || !this.videoElement) {
      return;
    }

    const google = window.google;

    this.adDisplayContainer = new google.ima.AdDisplayContainer(
      this.adContainer,
      this.videoElement
    );
    this.adDisplayContainer.initialize();

    this.adsLoader = new google.ima.AdsLoader(this.adDisplayContainer);
    this.adsLoader.addEventListener(
      google.ima.AdsManagerLoadedEvent.Type.ADS_MANAGER_LOADED,
      ((event: google.ima.AdsManagerLoadedEvent) => {
        this.onAdsManagerLoaded(event);
      }) as AdsLoaderEventHandler,
      false
    );

    this.adsLoader.addEventListener(
      google.ima.AdErrorEvent.Type.AD_ERROR,
      ((event: google.ima.AdErrorEvent) => {
        this.onAdError(event);
      }) as AdsLoaderEventHandler,

      false
    );

    this.requestAds();
  }

  /** Request ads from the ad server */
  requestAds(): void {
    if (!this.adsLoader || !window.google || !window.google.ima || !this.videoElement) return;

    const google = window.google;

    const adsRequest = new google.ima.AdsRequest();
    adsRequest.adTagUrl = this.adTagUrl;

    adsRequest.linearAdSlotWidth = this.videoElement.clientWidth;
    adsRequest.linearAdSlotHeight = this.videoElement.clientHeight;
    adsRequest.nonLinearAdSlotWidth = this.videoElement.clientWidth;
    adsRequest.nonLinearAdSlotHeight = this.videoElement.clientHeight;

    this.adsLoader.requestAds(adsRequest);
  }

  /**
   * Handle ads manager loaded event
   *
   * @param adsManagerLoadedEvent - The ads manager loaded event
   */
  onAdsManagerLoaded(adsManagerLoadedEvent: google.ima.AdsManagerLoadedEvent): void {
    if (!window.google || !window.google.ima || !this.videoElement) return;

    const google = window.google;

    const adsRenderingSettings = new google.ima.AdsRenderingSettings();
    adsRenderingSettings.restoreCustomPlaybackStateOnAdBreakComplete = true;

    this.adsManager = adsManagerLoadedEvent.getAdsManager(this.videoElement, adsRenderingSettings);

    this.addPlaybackEventListeners();
    this.adUiEventListeners();

    try {
      this.adsManager.init(
        this.videoElement.clientWidth,
        this.videoElement.clientHeight,
        google.ima.ViewMode.NORMAL as google.ima.ViewMode // TODO: find alternative to ViewMode
      );

      this.adsLoaded = true;
    } catch (adError) {
      console.error('AdsManager initialization error:', adError);
    }
  }

  /**
   * Handle ad error event
   *
   * @param adErrorEvent - The ad error event
   */
  onAdError(adErrorEvent: google.ima.AdErrorEvent): void {
    console.error('Ad error:', adErrorEvent.getError());
    if (this.adsManager) {
      this.adsManager.destroy();
    }
    this.isAdPlaying = false;

    if (this.videoElement instanceof HTMLMediaElement) {
      this.videoElement.play();
    }
  }

  /** Handle content pause requested event */
  onContentPauseRequested(): void {
    this.isAdPlaying = true;
    if (this.videoElement instanceof HTMLMediaElement) {
      this.videoElement.pause();
    }
    this.enableAdContainerPointerEvents();
  }

  /** Handle content resume requested event */
  onContentResumeRequested(): void {
    this.isAdPlaying = false;
    if (this.videoElement instanceof HTMLMediaElement) {
      this.videoElement.play();
    }
    this.disableAdContainerPointerEvents();
  }

  /** Handle all ads completed event */
  onAllAdsCompleted(): void {
    this.isAdPlaying = false;
    this.disableAdContainerPointerEvents();
  }

  /** Handle ad loaded event */
  onAdLoaded(): void {
    console.log('Ad loaded');
  }

  /** Handle ad started event */
  onAdStarted(): void {
    this.enableAdContainerPointerEvents();
  }

  /** Handle ad complete event */
  onAdComplete(): void {
    this.disableAdContainerPointerEvents();
  }

  /** Play ads */
  playAds(): void {
    if (!this.adsLoaded || !this.adsManager) return;

    try {
      this.adsManager.start();
    } catch (adError) {
      console.error('AdsManager start error:', adError);
      if (this.videoElement instanceof HTMLMediaElement) {
        this.videoElement.play();
      }
    }
  }

  /** Resize the ads manager when the window or video element size changes */
  resize(): void {
    if (!this.adsManager || !this.videoElement || !window.google || !window.google.ima) return;

    try {
      const google = window.google;
      this.adsManager.resize(
        this.videoElement.clientWidth,
        this.videoElement.clientHeight,
        google.ima.ViewMode.NORMAL as google.ima.ViewMode // TODO: find alternative to ViewMode
      );
    } catch (adError) {
      console.error('AdsManager resize error:', adError);
    }
  }

  /** Clean up the ads manager */
  destroy(): void {
    if (this.adsManager) {
      this.adsManager.destroy();
      this.adsManager = undefined;
    }

    if (this.adsLoader) {
      this.adsLoader.destroy();
      this.adsLoader = undefined;
    }

    if (this.adContainer && this.adContainer.parentNode) {
      this.adContainer.parentNode.removeChild(this.adContainer);
      this.adContainer = undefined;
    }

    this.isAdPlaying = false;
    this.adsLoaded = false;
  }

  private disableAdContainerPointerEvents() {
    if (this.adContainer) {
      this.adContainer.style.pointerEvents = 'none';
    }
  }

  private enableAdContainerPointerEvents() {
    if (this.adContainer) {
      this.adContainer.style.pointerEvents = 'auto';
    }
  }

  private adUiEventListeners() {
    if (this.adsManager === undefined) return;
    this.adsManager.addEventListener(google.ima.AdEvent.Type.LOADED, (() => {
      this.onAdLoaded();
    }) as ImaAdEventHandler);
    this.adsManager.addEventListener(google.ima.AdEvent.Type.STARTED, (() => {
      this.onAdStarted();
    }) as ImaAdEventHandler);
    this.adsManager.addEventListener(google.ima.AdEvent.Type.COMPLETE, (() => {
      this.onAdComplete();
    }) as ImaAdEventHandler);
  }

  private addPlaybackEventListeners() {
    if (this.adsManager === undefined) return;
    this.adsManager.addEventListener(google.ima.AdEvent.Type.CONTENT_PAUSE_REQUESTED, (() => {
      this.onContentPauseRequested();
    }) as ImaAdEventHandler);
    this.adsManager.addEventListener(google.ima.AdEvent.Type.CONTENT_RESUME_REQUESTED, (() => {
      this.onContentResumeRequested();
    }) as ImaAdEventHandler);
    this.adsManager.addEventListener(google.ima.AdEvent.Type.ALL_ADS_COMPLETED, (() => {
      this.onAllAdsCompleted();
    }) as ImaAdEventHandler);
    this.adsManager.addEventListener(google.ima.AdErrorEvent.Type.AD_ERROR, ((
      event: google.ima.AdErrorEvent
    ) => {
      this.onAdError(event);
    }) as ImaAdEventHandler);
  }

  private initializeImaScript() {
    if (!window.google || !window.google.ima) {
      this.loadImaScript()
        .then(() => {
          this.setupIma();
        })
        .catch(error => {
          console.error('Failed to load IMA SDK:', error);
        });
    } else {
      this.setupIma();
    }
  }
}
